
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { VueBootstrapField } from "@/store/types";
import { ReportState, ReportHistoryModal, ReportTypes, Types } from "@/store/reports/types";
import InsightReportsApi, { reportApiResponse, ReportHistoryData } from "@/services/api.insightReports.service.ts";
import ConfirmationModal from "@/components-shared/ConfirmationModal.vue";
import { parseDateTime, decodeUrlParameters }  from "@/utils/reports-utils";

const Reports = namespace("reports");

@Component({
	components: {
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"confirmation-modal": ConfirmationModal
	}
})
export default class HistoryModal extends Vue {
	@Reports.Getter("getReportTypes") reportTypes;
	@Reports.Mutation setReportHistoryModalShown: (historyModal: ReportHistoryModal) => void;
	@Reports.State((state: ReportState) => state.ReportHistoryModal) private reportHistoryModal: ReportHistoryModal;

	// Fields for the btable.
	private readonly fields: VueBootstrapField[] = [
		{ key: "reportName", label: "Report", sortable: false },
		{ key: "runDateTime", label: "Date/Time", sortable: false },
		{ key: "actions", label: "Actions", sortable: false }
	];

	private loadingHistory: boolean = false;
	private pagesLoaded: number = 0;
	private allPagesLoaded: boolean = false;
	private records: ReportHistoryData[] = [];

	private showConfirmationModal: boolean = false;
	private confirmModalOkButtonText: string = null;
	private confirmModalCancelButtonText: string = null;
	private confirmModalDeletion: boolean = false;
	private confirmModalTitle: string = "Confirmation Modal";
	private confirmationModalMessage: string = "";

	private deleteReportHistoryID: number = 0;

	@Watch("reportHistoryModal.shown")
	async onModalShown(modalShown: boolean): Promise<void> {
		// Load the first page of history results.
		if (modalShown){
			this.getHistoryForPage();
		}
	}

	/**
	 * Loads the history for the given page.
	 */
	private async getHistoryForPage(): Promise<void> {
		this.loadingHistory = true;
		this.pagesLoaded++;

		var results: ReportHistoryData[] = (await InsightReportsApi.getReportHistory(this.pagesLoaded))
			.result as ReportHistoryData[];

		if (results != null && results.length > 0) {
			this.records.push.apply(this.records, results);
		} else {
			this.allPagesLoaded = true;
		}

		this.loadingHistory = false;
	}

	private async resetHistoryForPage(): Promise<void> {
		this.loadingHistory = true;
		this.pagesLoaded = 1;

		this.records = [];

		var results: ReportHistoryData[] = (await InsightReportsApi.getReportHistory(this.pagesLoaded))
			.result as ReportHistoryData[];

		if (results != null && results.length > 0) {
			this.records.push.apply(this.records, results);
		}

		this.loadingHistory = false;
	}

	/**
	 * Finds the matching report type by its endpoint or URL.
	 * @param endpoint  the endpoint string of the insight report (with or without query parameters).
	 */
	private findReportTypeByEndpoint(endpoint: string): Types {
		let result: Types = null;

		// Iterate through report categories and types. Return the first type that is contained in the endpoint url.
		for (var i = 0; i < this.reportTypes.length; i++) {
			for (var y = 0; y < this.reportTypes[i].types.length; y++) {
				if (endpoint.includes(this.reportTypes[i].types[y].endpoint)) {
					result = this.reportTypes[i].types[y];
					break;
				}
			}

			if (result) {
				break;
			} // If we found it, break out.
		}

		return result;
	}

	/**
	 * Runs the selected report from the list of report history.
	 * @param item  the selected item from the table as a ReportHistoryData type.
	 */
	private runReport(url: string): void {
		if (!url) {
			throw "Failed to run report history record.";
		}

		url = url.slice(url.indexOf("/reports/"), url.length);

		// Run the report...
		this.$router.push(url);

		this.closeHistoryModal();
	}

	/**
	 * Retreives the name of the report by eximining the URL.
	 * @param item  the selected item from the table as a ReportHistoryData type.
	 */
	private getReportNameFromEndpoint(item: ReportHistoryData): string {		
		if (!item || !item.url) {
			return "Not Available";
		}

		var report: Types = this.findReportTypeByEndpoint(
			item.url.slice(item.url.indexOf("/reports/") + 8, item.url.length)
		);

		if (!report) {
			return "Not Available";
		}

		return report.title;

	}
	
	/**
	 * Handles the infinite scroll of the btable.
	 */
	private async handleScrollBarReachEnd(): Promise<void> {
		if (this.loadingHistory || this.allPagesLoaded || !this.reportHistoryModal.shown) {
			return;
		}

		await this.getHistoryForPage();
	}

	/**
	 * Closes the modal by setting the ReportHistoryModal in the Reports state.
	 */
	private closeHistoryModal() {
		this.setReportHistoryModalShown({ shown: false });
		this.pagesLoaded = 0;
		this.records = [];
		this.allPagesLoaded = false;
	}

	private openConfirmationModal(
		title: string,
		deletion: boolean,
		okText: string,
		cancelText: string,
		standardMessage: string
	): void {
		this.confirmModalDeletion = deletion;
		this.confirmModalOkButtonText = okText;
		this.confirmModalCancelButtonText = cancelText;
		this.confirmModalTitle = title;
		this.showConfirmationModal = true;
		this.confirmationModalMessage = standardMessage;
	}

	private closeConfirmationModal(): void {
		this.showConfirmationModal = false;
		this.confirmModalDeletion = false;
		this.confirmModalOkButtonText = "";
		this.confirmModalCancelButtonText = "OK";
		this.confirmModalTitle = "";
	}

	private confirmRemoveHistory(historyID: number): void {
		this.deleteReportHistoryID = historyID;
		let message: string = "Are you sure you want to remove this record?";
		this.openConfirmationModal("Delete Report History", true, "DELETE", "Cancel", message);
	}

	private async removeHistory(): Promise<void> {
		let response: reportApiResponse;
		response = await InsightReportsApi.deleteReportHistory(this.deleteReportHistoryID);

		if (response.success == true) {
			//If response is successful refresh modal
			await this.resetHistoryForPage();
		}

		this.deleteReportHistoryID = 0;
		this.closeConfirmationModal();
	}

	/**
	 * Formats the date to a more readable format.
	 * @param item  the selected item from the table as a ReportHistoryData type.
	 */
	private getDateTime(item: ReportHistoryData) : string {
		return parseDateTime(item.runDateTime);
	}
	
}
